<script setup lang="ts">
import CheckboxOffSvg from '@/assets/svg/checkbox-off.svg?component';
import CheckboxOnSvg from '@/assets/svg/checkbox-on.svg?component';
import CheckboxOnDisabledSvg from '@/assets/svg/checkbox-on-disabled.svg?component';
import CheckboxMinusSvg from '@/assets/svg/checkbox-minus.svg?component';
import { computed } from 'vue';

const props = defineProps<{
  modelValue: boolean;
  isMinusCheck?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'click', value: Event): void;
}>();

const handleToggle = (event: Event) => {
  if (isDisabled.value === true) return;
  emit('update:modelValue', !props.modelValue);
  emit('click', event);
};

const isDisabled = computed(() => props.disabled === true);
// Existing callers don't pass ariaLabel or listen for keyboard-triggered emits,
// so only opt in to checkbox role/tabindex/keydown when a label is supplied.
const isInteractive = computed(() => !!props.ariaLabel);

const handleKeydown = (event: KeyboardEvent) => {
  if (!isInteractive.value) return;
  if (event.key !== ' ' && event.key !== 'Enter') return;
  // Prevent Space from scrolling the page while this checkbox has focus.
  event.preventDefault();
  handleToggle(event);
};
</script>

<template>
  <div
    :role="isInteractive ? 'checkbox' : undefined"
    :aria-checked="isInteractive ? modelValue : undefined"
    :aria-disabled="isInteractive ? isDisabled : undefined"
    :aria-label="ariaLabel"
    :tabindex="isInteractive ? (isDisabled ? -1 : 0) : undefined"
    :class="{
      'cursor-pointer': !isDisabled,
      'cursor-not-allowed': isDisabled,
      'opacity-60': isDisabled && !modelValue,
    }"
    @click="handleToggle"
    @keydown="handleKeydown"
    data-testid="input-checkbox"
  >
    <template v-if="modelValue">
      <CheckboxMinusSvg v-if="isMinusCheck" data-testid="input-checkbox-minus" />
      <CheckboxOnDisabledSvg v-else-if="isDisabled" data-testid="input-checkbox-on-disabled" />
      <CheckboxOnSvg v-else data-testid="input-checkbox-on" />
    </template>

    <template v-else>
      <CheckboxOffSvg data-testid="input-checkbox-off" />
    </template>
  </div>
</template>
